import {
  ArrowRightIcon,
  ChevronDown,
  Download,
  FileUp,
  PlusIcon,
  Trash2,
  Upload,
} from 'lucide-react';
import { Checkbox, Empty, Modal, Pagination, Select, Spin, Tooltip } from 'antd';
import { AnimatePresence } from 'framer-motion';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  ButtonWizard,
  ConfirmationDialog,
  HeadingWizard,
  GlobalLoader,
} from '../..';
import MultiSelectInput from '../../formFields/MultiSelectInput';
import SelectInput from '../../formFields/SelectInput';
import { useNotification } from '../../../contexts';
import { CardWizard, SideSheet, SideSheetHeader } from '../../../layouts';
import {
  useCreateCustomerMaterialMappingMutation,
  useBulkDeleteCustomerMaterialMappingMutation,
  useGetCustomerMaterialMappingsQuery,
  useLazyDownloadSampleCustomerMaterialMappingQuery,
  useLazyExportCustomerMaterialMappingQuery,
  useUploadCustomerMaterialMappingMutation,
} from '../../../store/rtkQuery/travelReq';
import {
  useGetCustomerQuery,
  useGetMaterialQuery,
} from '../../../store/rtkQuery/master';
import {
  extractErrorMessageFromAPI,
  formatDayJsDate,
  BusinessMappingFilterStorageKeys,
  getSavedFilters,
  saveFilters,
} from '../../../utils';
import dayjs from 'dayjs';

type CustomerMaterialMappingFilters = {
  page: number;
  pageSize: number;
  customerId?: number | null;
  materialId?: number | null;
};

type CustomerMaterialMappingForm = {
  customerId: number | null;
  materialIds: number[];
};

const DEFAULT_FORM_VALUES: CustomerMaterialMappingForm = {
  customerId: null,
  materialIds: [],
};

const getValue = (item: any, keys: string[]) => {
  for (const key of keys) {
    const value = item?.[key];
    if (value !== null && value !== undefined && value !== '') {
      return value;
    }
  }
  return '';
};

const getList = (payload: any) => {
  if (Array.isArray(payload?.list)) return payload.list;
  if (Array.isArray(payload?.data?.list)) return payload.data.list;
  if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const getTotal = (payload: any, fallback: number) =>
  payload?.count ??
  payload?.data?.count ??
  payload?.data?.total ??
  payload?.data?.totalCount ??
  fallback;

const getCustomerLabel = (customer: any) => {
  const code = getValue(customer, ['customerCode', 'CustomerCode', 'code', 'Code', 'erpCode']);
  const name = getValue(customer, ['name', 'Name', 'customerName', 'CustomerName', 'FullName']);
  return [code, name].filter(Boolean).join(' - ') || '-';
};

const getMaterialLabel = (material: any) => {
  const code = getValue(material, [
    'materialCode',
    'MaterialCode',
    'materialSapCode',
    'SapCode',
    'code',
    'Code',
    'erpCode',
  ]);
  const name = getValue(material, [
    'name',
    'Name',
    'materialName',
    'MaterialName',
    'ShortDescription',
    'LongDescription',
    'description',
  ]);
  return [code, name].filter(Boolean).join(' - ') || '-';
};

const formatMappingDate = (value: string | null | undefined) =>
  value ? formatDayJsDate(dayjs(value), 'verbose') : '-';

const getUserLabel = (user: any) =>
  user?.name || user?.FullName || user?.email || '-';

const getRowCustomerLabel = (row: any) =>
  row?.customer
    ? getCustomerLabel(row.customer)
    : [
        getValue(row, [
          'customerName',
          'CustomerName',
          'customer',
        ]),
        getValue(row, [
          'customerCode',
          'CustomerCode',
          'customerErpCode',
          'CustomerErpCode',
        ]),
      ]
        .filter(Boolean)
        .join(' - ') ||
      getValue(row, [
        'customerName',
        'CustomerName',
        'customer',
        'customerCode',
        'CustomerCode',
      ]) ||
      '-';

const getMaterialRows = (row: any) => {
  if (Array.isArray(row?.materials)) return row.materials;
  if (Array.isArray(row?.material)) return row.material;
  if (row?.material) return [row.material];
  return [];
};

const getCustomerId = (row: any) =>
  Number(getValue(row, ['customerId', 'CustomerID', 'id']) || row?.customer?.id || row?.customer?.ID);

const getMappingId = (materialRow: any) =>
  Number(
    getValue(materialRow, [
      'mappingId',
      'customerMaterialMappingId',
      'customerMaterialMappingID',
      'id',
      'ID',
    ]),
  );

const getMaterialId = (materialRow: any) =>
  Number(
    getValue(materialRow, ['materialId', 'MaterialID']) ||
      materialRow?.material?.id ||
      materialRow?.material?.ID ||
      materialRow?.id ||
      materialRow?.ID,
  );

const cleanFilters = (filters: CustomerMaterialMappingFilters) =>
  Object.fromEntries(
    Object.entries(filters).filter(
      ([, value]) => value !== null && value !== undefined && value !== '',
    ),
  );

const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  window.URL.revokeObjectURL(url);
};

export default function CustomerMaterialMapping() {
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const defaultFilters: CustomerMaterialMappingFilters = {
    page: 1,
    pageSize: 25,
    customerId: null,
    materialId: null,
  };
  const [appliedFilters, setAppliedFilters] =
    useState<CustomerMaterialMappingFilters>(() =>
      getSavedFilters(BusinessMappingFilterStorageKeys.CUSTOMER_MATERIAL, defaultFilters),
    );
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [selectedMaterialIds, setSelectedMaterialIds] = useState<
    Record<number, Record<number, boolean>>
  >({});
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const form = useForm<CustomerMaterialMappingForm>({
    defaultValues: DEFAULT_FORM_VALUES,
  });

  const customerResponse = useGetCustomerQuery({});
  const materialResponse = useGetMaterialQuery({});
  const mappingResponse = useGetCustomerMaterialMappingsQuery(appliedFilters);
  const [createMapping, createMappingResponse] =
    useCreateCustomerMaterialMappingMutation();
  const [bulkDeleteMapping, bulkDeleteMappingResponse] =
    useBulkDeleteCustomerMaterialMappingMutation();
  const [exportMapping, exportMappingResponse] =
    useLazyExportCustomerMaterialMappingQuery();
  const [downloadSample, downloadSampleResponse] =
    useLazyDownloadSampleCustomerMaterialMappingQuery();
  const [uploadMapping, uploadMappingResponse] =
    useUploadCustomerMaterialMappingMutation();

  const customers = getList(customerResponse.data);
  const materials = getList(materialResponse.data);
  const rows = getList(mappingResponse.data);
  const total = getTotal(mappingResponse.data, rows.length);

  useEffect(() => {
    saveFilters(BusinessMappingFilterStorageKeys.CUSTOMER_MATERIAL, appliedFilters);
  }, [appliedFilters]);

  const customerOptions = useMemo(
    () =>
      customers.map((customer: any) => ({
        value: Number(getValue(customer, ['id', 'ID', 'customerId'])),
        label: getCustomerLabel(customer),
      })),
    [customers],
  );

  const materialOptions = useMemo(
    () =>
      materials.map((material: any) => ({
        value: Number(getValue(material, ['id', 'ID', 'materialId'])),
        label: getMaterialLabel(material),
      })),
    [materials],
  );

  const selectedCustomerId = form.watch('customerId');

  const handleFilterChange = (
    key: keyof CustomerMaterialMappingFilters,
    value: number | null,
  ) => {
    setAppliedFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1,
    }));
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setAppliedFilters(prev => ({
      ...prev,
      page,
      pageSize,
    }));
  };

  const handleSubmit = async (data: CustomerMaterialMappingForm) => {
    if (!data.customerId) {
      return showNotification('warning', 'Warning', 'Please select customer.');
    }
    if (!data.materialIds?.length) {
      return showNotification('warning', 'Warning', 'Please select material.');
    }
    try {
      await createMapping({
        customerId: Number(data.customerId),
        materialIds: data.materialIds.map(Number),
      }).unwrap();
      showNotification(
        'success',
        'Success',
        'Customer material mapping saved successfully.',
      );
      setIsSheetOpen(false);
      form.reset(DEFAULT_FORM_VALUES);
    } catch (error) {
      showNotification(
        'error',
        'Error',
        extractErrorMessageFromAPI(error as any) ||
          'Failed to save customer material mapping.',
      );
    }
  };

  const handleExport = async () => {
    try {
      const blob = await exportMapping(cleanFilters(appliedFilters)).unwrap();
      downloadBlob(blob, 'customer_material_mapping.xlsx');
      showNotification('success', 'Success', 'Export downloaded successfully.');
    } catch (error) {
      showNotification(
        'error',
        'Error',
        extractErrorMessageFromAPI(error as any) || 'Failed to export data.',
      );
    }
  };

  const handleDownloadSample = async () => {
    try {
      const blob = await downloadSample().unwrap();
      downloadBlob(blob, 'customer_material_mapping_sample.xlsx');
    } catch (error) {
      showNotification(
        'error',
        'Error',
        extractErrorMessageFromAPI(error as any) ||
          'Failed to download sample Excel.',
      );
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      return showNotification('error', 'Error', 'Please select a file first.');
    }

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const response = await uploadMapping(formData).unwrap();
      showNotification(
        'success',
        'Success',
        response?.message || 'Customer material mapping uploaded successfully.',
      );
      setSelectedFile(null);
      setShowExcelModal(false);
    } catch (error) {
      showNotification(
        'error',
        'Error',
        extractErrorMessageFromAPI(error as any) || 'Failed to upload file.',
      );
    }
  };

  const toggleRow = (rowId: string) => {
    setExpandedRows(prev => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const toggleMaterialSelection = (customerId: number, materialId: number) => {
    setSelectedMaterialIds(prev => ({
      ...prev,
      [customerId]: {
        ...(prev[customerId] || {}),
        [materialId]: !prev[customerId]?.[materialId],
      },
    }));
  };

  const getSelectedMaterialIds = (customerId: number) =>
    Object.entries(selectedMaterialIds[customerId] || {})
      .filter(([, isSelected]) => isSelected)
      .map(([materialId]) => Number(materialId));

  const openAddSheet = () => {
    form.reset(DEFAULT_FORM_VALUES);
    setIsSheetOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.customerId || !deleteTarget?.materialIds?.length) return;

    try {
      await bulkDeleteMapping({
        customerId: deleteTarget.customerId,
        materialIds: deleteTarget.materialIds,
      }).unwrap();
      showNotification(
        'success',
        'Success',
        'Customer material mapping deleted successfully.',
      );
      setSelectedMaterialIds(prev => ({
        ...prev,
        [deleteTarget.customerId]: {},
      }));
      setDeleteTarget(null);
    } catch (error) {
      showNotification(
        'error',
        'Error',
        extractErrorMessageFromAPI(error as any) ||
          'Failed to delete customer material mapping.',
      );
    }
  };

  return (
    <div className="p-1.5 flex flex-col gap-2">
      <Spin spinning={mappingResponse.isFetching}>
        <CardWizard addOnClass="p-0 pt-2.5">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 mb-3">
            <HeadingWizard addOnClass="text-[#6E6893] font-extrabold uppercase tracking-wider text-sm">
              Customer Material Mapping
            </HeadingWizard>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Customer"
                value={appliedFilters.customerId}
                options={customerOptions}
                onChange={value => handleFilterChange('customerId', value)}
                className="w-56"
              />
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Material"
                value={appliedFilters.materialId}
                options={materialOptions}
                onChange={value => handleFilterChange('materialId', value)}
                className="w-56"
              />
              <ButtonWizard
                addOnClass="border border-primary-600 text-primary-600"
                addOnTextClass="text-primary-600"
                buttonText="Export"
                onBtnClick={handleExport}
                loading={exportMappingResponse.isFetching}
              >
                <Download className="w-4 h-4 text-primary-600" />
              </ButtonWizard>
              <ButtonWizard
                addOnClass="border border-primary-600 text-primary-600"
                addOnTextClass="text-primary-600"
                onBtnClick={() => setShowExcelModal(true)}
              >
                <Tooltip title="Download & Upload Excel" placement="top">
                  <FileUp className="w-4 h-4 text-primary-600" />
                </Tooltip>
              </ButtonWizard>
              <ButtonWizard
                addOnClass="bg-primary-600 h-8 px-4"
                buttonText="Add"
                onBtnClick={openAddSheet}
              >
                <PlusIcon className="size-4 stroke-[3px] text-white" />
              </ButtonWizard>
              <span
                onClick={() => navigate(-1)}
                className="cursor-pointer text-xs px-2 py-2 rounded-md text-[#4649EC] font-semibold hover:opacity-80 hover:underline transition-colors flex items-center gap-1 w-max border border-[#4649EC]"
              >
                <span>Back</span>
                <ArrowRightIcon className="size-3 stroke-[4px] text-[#4649EC]" />
              </span>
            </div>
          </div>

          <div className="flex flex-col">
            <div className="relative overflow-x-auto shadow border border-gray-200 min-h-[calc(100vh-10rem)] max-h-[calc(100vh-10rem)]">
              {rows.length > 0 ? (
                <table className="min-w-full text-sm text-gray-800 border-spacing-0">
                  <thead
                    className="sticky top-0 z-[11] shadow-sm border-b border-gray-300"
                    style={{ backgroundColor: '#F4F2FF' }}
                  >
                    <tr>
                      <th className="px-4 py-3 text-sm font-semibold text-left text-[#6E6893] w-14" />
                      <th className="px-4 py-3 text-sm font-semibold text-left text-[#6E6893] whitespace-nowrap">
                        Customer
                      </th>
                      <th className="px-4 py-3 text-sm font-semibold text-left text-[#6E6893] whitespace-nowrap">
                        Materials Linked
                      </th>
                      <th className="px-4 py-3 text-sm font-semibold text-left text-[#6E6893] whitespace-nowrap">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {rows.map((row: any, index: number) => {
                      const rowId = String(row?.id ?? row?.customerId ?? index);
                      const materialRows = getMaterialRows(row);
                      const rowCustomerId = getCustomerId(row);
                      const selectedIdsForCustomer =
                        getSelectedMaterialIds(rowCustomerId);
                      const isExpanded = expandedRows[rowId];

                      return (
                        <Fragment key={rowId}>
                          <tr
                            className="hover:bg-gray-50 transition-colors duration-150"
                          >
                            <td className="px-4 py-3 text-xs text-gray-700 border-b border-gray-200">
                              <button
                                type="button"
                                onClick={() => toggleRow(rowId)}
                                className="grid size-7 place-items-center rounded border border-gray-200 text-[#6E6893]"
                              >
                                <ChevronDown
                                  className={`size-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                />
                              </button>
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-700 font-medium border-b border-gray-200 whitespace-nowrap">
                              {getRowCustomerLabel(row)}
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-700 border-b border-gray-200 whitespace-nowrap">
                              {materialRows.length}
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-700 border-b border-gray-200 whitespace-nowrap">
                              <ButtonWizard
                                addOnClass="border border-primary-600 text-primary-600 shadow-none px-2 py-1 gap-1"
                                addOnTextClass="text-primary-600 text-[11px]"
                                size="small"
                                buttonText="Add Material"
                                onBtnClick={() => {
                                  form.reset({
                                    customerId: getCustomerId(row),
                                    materialIds: [],
                                  });
                                  setIsSheetOpen(true);
                                }}
                              >
                                <PlusIcon className="size-3 text-primary-600" />
                              </ButtonWizard>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr className="bg-gray-50">
                              <td />
                              <td colSpan={3} className="px-4 py-3">
                                {materialRows.length > 0 ? (
                                  <div className="flex flex-col gap-2">
                                    {selectedIdsForCustomer.length > 0 && (
                                      <div className="flex justify-end">
                                        <ButtonWizard
                                          addOnClass="border border-error-500 text-error-500 shadow-none px-2 py-1 gap-1"
                                          addOnTextClass="text-error-500 text-[11px]"
                                          size="small"
                                          buttonText={`Delete Selected (${selectedIdsForCustomer.length})`}
                                          onBtnClick={() =>
                                            setDeleteTarget({
                                              customerId: rowCustomerId,
                                              materialIds:
                                                selectedIdsForCustomer,
                                              label: `${selectedIdsForCustomer.length} selected materials`,
                                            })
                                          }
                                        >
                                          <Trash2 className="size-3 text-error-500" />
                                        </ButtonWizard>
                                      </div>
                                    )}
                                    <div className="overflow-x-auto rounded border border-gray-200 bg-white">
                                      <table className="min-w-full border-spacing-0 text-xs">
                                        <thead className="bg-[#F4F2FF] text-[#6E6893]">
                                          <tr>
                                            <th className="px-3 py-2 text-left font-semibold whitespace-nowrap w-10" />
                                            <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">
                                              Material Code
                                            </th>
                                            <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">
                                              Material Name
                                            </th>
                                            <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">
                                              Created At
                                            </th>
                                            <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">
                                              Created By
                                            </th>
                                            <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">
                                              Action
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                          {materialRows.map(
                                            (
                                              material: any,
                                              materialIndex: number,
                                            ) => {
                                              const materialId =
                                                getMaterialId(material);
                                              return (
                                              <tr
                                                key={
                                                  getMappingId(material) ||
                                                  material?.materialId ||
                                                  materialIndex
                                                }
                                                className="hover:bg-gray-50"
                                              >
                                                <td className="px-3 py-2 whitespace-nowrap">
                                                  <Checkbox
                                                    checked={
                                                      !!selectedMaterialIds[
                                                        rowCustomerId
                                                      ]?.[materialId]
                                                    }
                                                    disabled={
                                                      !rowCustomerId ||
                                                      !materialId
                                                    }
                                                    onChange={() =>
                                                      toggleMaterialSelection(
                                                        rowCustomerId,
                                                        materialId,
                                                      )
                                                    }
                                                  />
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap">
                                                  {getValue(material, [
                                                    'materialCode',
                                                    'MaterialCode',
                                                  ]) || '-'}
                                                </td>
                                                <td className="px-3 py-2 min-w-64">
                                                  {getValue(material, [
                                                    'materialName',
                                                    'MaterialName',
                                                    'name',
                                                    'LongDescription',
                                                    'ShortDescription',
                                                  ]) || '-'}
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap">
                                                  {formatMappingDate(
                                                    material?.createdAt,
                                                  )}
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap">
                                                  {getUserLabel(
                                                    material?.createdBy,
                                                  )}
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap">
                                                  <div className="flex items-center gap-2">
                                                    <Tooltip
                                                      title={
                                                        getCustomerId(row) &&
                                                        getMaterialId(material)
                                                          ? 'Delete mapping'
                                                          : 'Mapping details missing'
                                                      }
                                                    >
                                                      <button
                                                        type="button"
                                                        disabled={
                                                          !getCustomerId(row) ||
                                                          !getMaterialId(
                                                            material,
                                                          )
                                                        }
                                                        onClick={() =>
                                                          setDeleteTarget({
                                                            customerId:
                                                              getCustomerId(row),
                                                            materialIds: [
                                                              getMaterialId(
                                                                material,
                                                              ),
                                                            ],
                                                            label:
                                                              getMaterialLabel(
                                                                material?.material ??
                                                                  material,
                                                              ),
                                                          })
                                                        }
                                                        className="grid size-7 place-items-center rounded border border-error-500 text-error-500 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
                                                      >
                                                        <Trash2 className="size-3.5" />
                                                      </button>
                                                    </Tooltip>
                                                  </div>
                                                </td>
                                              </tr>
                                            );
                                            },
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-xs text-gray-500">
                                    No material linked
                                  </span>
                                )}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="grid place-items-center h-full">
                  <Empty />
                </div>
              )}
            </div>
            {rows.length > 0 && (
              <div className="flex justify-between bg-[#F2F0F9] items-center px-4 py-2.5 text-[#6E6893]">
                <Pagination
                  size="small"
                  total={total}
                  pageSize={appliedFilters.pageSize}
                  current={appliedFilters.page}
                  pageSizeOptions={[25, 50, 100, 200]}
                  showSizeChanger
                  showTotal={value => (
                    <span className="text-xs text-[#6E6893] font-semibold tracking-wide">
                      Total {value} items
                    </span>
                  )}
                  onChange={handlePageChange}
                  onShowSizeChange={handlePageChange}
                />
              </div>
            )}
          </div>
        </CardWizard>
      </Spin>

      <AnimatePresence>
        {isSheetOpen && (
          <SideSheet
            isOpen={isSheetOpen}
            addOnClass="w-[50vw] sm:w-[30vw] max-w-unset"
            onClose={() => setIsSheetOpen(false)}
            headerChildren={
              <div className="border-b border-gray-100">
                <SideSheetHeader
                  title={
                    'Add Customer Material Mapping'
                  }
                  onCloseClick={() => {
                    setIsSheetOpen(false);
                  }}
                />
              </div>
            }
            contentChildren={
              <div className="p-4 flex flex-col gap-4">
                <SelectInput<CustomerMaterialMappingForm>
                  name="customerId"
                  label="Customer"
                  control={form.control}
                  formState={form.formState}
                  rules={{ required: 'Customer is required' }}
                  config={{
                    allowClear: true,
                    showSearch: true,
                    optionFilterProp: 'label',
                    placeholder: 'Select customer',
                    options: customerOptions,
                  }}
                  onChange={() => form.setValue('materialIds', [])}
                />
                <MultiSelectInput<CustomerMaterialMappingForm>
                  name="materialIds"
                  label="Material"
                  control={form.control}
                  formState={form.formState}
                  rules={{ required: 'Material is required' }}
                  options={materialOptions}
                  config={{
                    disabled: !selectedCustomerId,
                    placeholder: selectedCustomerId
                      ? 'Select materials'
                      : 'Select customer first',
                    optionFilterProp: 'label',
                  }}
                />
              </div>
            }
            footerChildren={
              <div className="p-4 border-t">
                <div className="border-gray-100 flex items-center gap-4">
                  <ButtonWizard
                    addOnClass="bg-white border border-primary-600"
                    addOnTextClass="text-primary-600"
                    buttonText="Cancel"
                    onBtnClick={() => {
                      form.reset(DEFAULT_FORM_VALUES);
                      setIsSheetOpen(false);
                    }}
                    disabled={createMappingResponse.isLoading}
                  />
                  <ButtonWizard
                    addOnClass="bg-primary-600"
                    buttonText="Save"
                    onBtnClick={form.handleSubmit(handleSubmit)}
                    loading={createMappingResponse.isLoading}
                  />
                </div>
              </div>
            }
          />
        )}
      </AnimatePresence>

      <Modal
        open={showExcelModal}
        onCancel={() => setShowExcelModal(false)}
        footer={null}
        width={640}
        centered
        title="Excel Configuration"
      >
        <Spin
          spinning={
            downloadSampleResponse.isFetching || uploadMappingResponse.isLoading
          }
        >
          <div className="mt-4 space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-md px-4 py-3 text-xs text-blue-700">
              Please download the template first. Accepted headers are customer code/customerCode and material code/materialCode.
            </div>
            <button
              onClick={handleDownloadSample}
              className="w-full flex items-center justify-center gap-2 border border-gray-400 text-gray-800 hover:bg-gray-50 hover:border-gray-500 px-4 py-2 rounded-md text-sm font-medium transition-colors"
            >
              <Download className="w-4 h-4" />
              Download Excel
            </button>
            <div className="space-y-4">
              <label className="w-full flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-md px-4 py-8 cursor-pointer hover:border-gray-400 transition-colors text-center">
                <span className="text-sm text-gray-700">
                  Click to select file
                </span>
                <span className="text-xs text-gray-400 mt-1">
                  Supported formats: .xlsx, .xls
                </span>
                <input
                  type="file"
                  accept=".xlsx, .xls"
                  onChange={event =>
                    setSelectedFile(event.target.files?.[0] ?? null)
                  }
                  className="hidden"
                />
              </label>
              {selectedFile && (
                <div className="text-xs text-gray-600 truncate">
                  Selected File: {selectedFile.name}
                </div>
              )}
              <button
                onClick={handleUpload}
                disabled={!selectedFile}
                className="flex items-center justify-center gap-2 w-full bg-gray-800 hover:bg-gray-900 disabled:bg-gray-300 disabled:cursor-not-allowed px-4 py-2 rounded-md text-sm font-medium text-white"
              >
                <Upload className="w-4 h-4" />
                Upload File
              </button>
            </div>
          </div>
        </Spin>
      </Modal>

      <ConfirmationDialog
        loading={bulkDeleteMappingResponse.isLoading}
        title="Are you sure?"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
        isOpen={!!deleteTarget}
      >
        <p className="text-text-200 text-xs">
          Are you sure you want to delete {deleteTarget?.label || 'this mapping'}?
        </p>
      </ConfirmationDialog>

      <GlobalLoader
        loading={
          createMappingResponse.isLoading ||
          bulkDeleteMappingResponse.isLoading ||
          exportMappingResponse.isFetching
        }
      />
    </div>
  );
}
